/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ToastOptions, toast as Toast } from 'react-toastify';
import React from 'react';
import { getUserData } from 'src/api/services/user/user.get';

const toast = (() => {
  const options: ToastOptions = {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: true
  };

  const format = (header: string, message?: any) => {
    const content = (
      <div>
        <div className='toast-text-bold'>{header}</div>
        {message != null ? <div>{message}</div> : null}
      </div>
    );
    return content;
  };

  return {
    default: (header: string, message?: any) => {
      Toast(format(header, message), options);
    },
    info: (header: string, message?: any) => {
      Toast.info(format(header, message), options);
    },
    success: (header: string, message?: any) => {
      Toast.success(format(header, message), options);
    },
    error: (header: string, message?: any) => {
      Toast.error(format(header, message), options);
    },
    warning: (header: string, message?: any) => {
      Toast.warn(format(header, message), options);
    }
  };
})();

const log = (() => {
  const style = {
    Parameter: { background: '#DCF1D0', color: '#2E4521' }, 
    Query:     { background: '#C9E2F7', color: '#184062' }, 
    Widget:    { background: '#4d852f', color: '#e7f6ea' }, 
    Procedure: { background: '#FBE2B6', color: '#5A3E16' }, 
    Function:  { background: '#333e8d', color: '#FFFFFF' }, 
    Service:   { background: '#946f56', color: '#FFFFFF' }, 
    Log:       { background: '#68615A', color: '#FFFFFF' }, 
    Rule:      { background: '#277A86', color: '#FFFFFF' }, 
    Workflow:  { background: '#6E3A78', color: '#FFFFFF' }, 
    Error:     { background: '#E83D30', color: '#FFFFFF' }, 
    Edge:      { background: '#2B7CEE', color: '#FFFFFF' }, 
    Event:     { background: '#557fc3', color: '#eceefc' }, 
    Info:     { background: '#b5ad65', color: '#32343f' },   
    // Form:      { background: '#C8EFE5', color: '#1F5145' }, 
    // Graph:     { background: '#CBEFF6', color: '#1F4951' }, 
    // Grid:      { background: '#FDE7B4', color: '#5A4116' }, 
    default:   { background: '#F7F5F3', color: '#10192D' } 
  } as const;

  const icons: Record<string, string> = {
    Procedure: '🗂️',
    Function: 'ƒ',
    Event: '🔔',
    // Form: '📝',
    // Graph: '📊',
    Parameter: '🧩',
    Query: '🔎',
    Widget: '🧩',
    Service: '🛠️',
    Rule: '📏',
    Workflow: '🔁',
    Edge: '➡️',
    Info: '➡️',
    Error: '❌',
    Log: '🧾',
    // Grid: '🔲',
    default: '•'
  };

  const chipCSS = (bg: string, fg: string) =>
    [
      `background:${bg}`,
      `color:${fg}`,
      'padding:2px 12px 2px 6px',
      'border-radius:9999px',
      'font-weight:650',
      'border:1px solid rgba(0,0,0,.08)',
      'box-shadow: 0 1px 0 rgba(255,255,255,.25) inset',
      'display:inline-block',
      'white-space:nowrap'
    ].join(';');

  const cssLabel =
    'white-space:nowrap;font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace;font-weight:600;color:#538aff;letter-spacing:.2px;padding-left:10px;';
  const cssCount =
    'margin-left:8px; padding:0 8px; border-radius:9999px; background:rgba(148,163,184,.12); color:#475569; border:1px solid rgba(148,163,184,.35); font-size:11px; line-height:18px; transform:translateY(-1px); white-space:nowrap;';

  const ellipsize = (s: string, n = 120) => (s?.length > n ? s.slice(0, n - 1) + '…' : s ?? '');

  // --- GATE: Error immer, alles andere nur wenn Admin & sessionStorage('logging') === 'true' ---
  const gate = (styleKey: keyof typeof style | string, run: () => void) => {
    if (String(styleKey) === 'Error') {
      run();
      return;
    }

    let enabled = false;
    try {
      enabled = sessionStorage.getItem('logging') === 'true';
    } catch {
      enabled = false;
    }
    if (!enabled) return;

    try {
      if (typeof getUserData === 'function') {
        Promise.resolve(getUserData()).then((user: any) => {
          if (user?.data?.user?.admin === true) run();
        }).catch(() => { });
      }
    } catch {
    }
  };

  const base = (header: string, message?: any, styleKey: keyof typeof style | string = 'default') => {
    const run = () => {
      const key = String(styleKey) as keyof typeof style;
      const resolvedKey: keyof typeof style = key in style ? key : 'default';

      const displayType = String(styleKey || 'default');
      const icon = icons[displayType] ?? icons.default;

      const chipText = `${icon}\u00A0${displayType}`;

      const fmtParts: string[] = [`%c ${chipText}`, `%c ${ellipsize(header)}`];
      const cssParts: string[] = [
        chipCSS(style[resolvedKey].background, style[resolvedKey].color),
        cssLabel
      ];

      let count: number | null = null;
      if (Array.isArray(message)) count = message.length;
      else if (message && typeof message === 'object') {
        const maybe = (message as any).count ?? (message as any).length;
        if (Number.isFinite(maybe)) count = Number(maybe);
      }
      if (count !== null) {
        fmtParts.push(`%c (${count})`);
        cssParts.push(cssCount);
      }

      if (typeof message !== 'undefined') {
        fmtParts.push('%o');
        console.log(fmtParts.join(' '), ...cssParts, message);
      } else {
        console.log(fmtParts.join(' '), ...cssParts);
      }
    };

    gate(styleKey, run);
  };

  (base as any).assert = (
    condition: boolean,
    header: string,
    data?: any,
    styleKey: keyof typeof style | string = 'Error'
  ) => {
    const run = () => {
      if (condition) return; 

      const key = String(styleKey) as keyof typeof style;
      const resolvedKey: keyof typeof style = key in style ? key : 'Error';
      const displayType = String(styleKey || 'Error');
      const icon = icons[displayType] ?? '⚠️';

      const chipText = `${icon}\u00A0${displayType}`;
      const fmt = `%c ${chipText} %c ${ellipsize(header)}`;
      const css = [chipCSS(style[resolvedKey].background, style[resolvedKey].color), cssLabel];

      console.assert(false, fmt, ...css);

      if (typeof data !== 'undefined') {
        console.groupCollapsed('Details');
        try {
          if (Array.isArray(data) || (data && typeof data === 'object')) {
            console.table(data as any);
          } else {
            console.log('%o', data);
          }
        } catch {
          console.log('%o', data);
        }
        console.trace();
        console.groupEnd();
      }
      console.count('Assert: ' + header);
    };

    gate(styleKey, run);
  };

  return base as any;
})();

export { toast, log };
